/**
 * PreRegistroFormModal — Reusable modal that wraps the pre-registro form.
 *
 * Used from GestionMedicosPage (button "+ Pre-registrar médico"). The form
 * is identical to the legacy /admin/pre-registro page but hosted inside a
 * Modal so admins don't need to navigate to a separate route.
 *
 * ROL es el PRIMER campo: el resto del formulario permanece oculto hasta que
 * se elige un rol, y los campos exclusivos de Médico/Supervisor (registro de
 * idoneidad, especialidad, centro médico, códigos SEIS) solo se muestran para
 * esos dos roles.
 */
import { type FormEvent, useState } from 'react';
import { where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, UserPlus } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { functions } from '../../config/firebase';
import { useCollection } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { MAX_30, soloAlfanumericoGuiones, soloDigitos } from '../../utils/inputFilters';
import styles from './PreRegistroFormModal.module.css';

const ESPECIALIDADES = [
  'Medicina General',
  'Pediatría',
  'Ginecología',
  'Cardiología',
  'Dermatología',
  'Psicología',
  'Psiquiatría',
  'Traumatología',
  'Neurología',
  'Urología',
  'Oftalmología',
  'Otorrinolaringología',
  'Otro',
];

const preRegistrarFn = httpsCallable(functions, 'portal_preRegistrarMedico');

type Role = '' | 'medico' | 'supervisor' | 'admin' | 'notificador';

const EMPTY_FORM = {
  role: '' as Role,
  email: '',
  telefono: '',
  nombre1: '',
  nombre2: '',
  apellido1: '',
  apellido2: '',
  cedula: '',
  // Exclusivos de Médico/Supervisor:
  colegiadoId: '', // "No. de Registro de Idoneidad"
  seisMedicoId: '',
  especialidad: '',
  seisEspecialidadId: '',
  subespecialidad: '',
  centroMedico: '',
  seisInstalacionId: '',
  salasAsignadas: [] as string[],
  // Segundo factor: se decide en el alta para que aplique desde el PRIMER
  // ingreso. Si se deja para después, el enrolamiento obligatorio del primer
  // login ya habrá ocurrido y la exención llega tarde.
  exentoSegundoFactor: false,
  motivoExencionSegundoFactor: '',
};

export interface PreRegistroFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful pre-registration. */
  onSuccess?: () => void;
}

export function PreRegistroFormModal({ open, onClose, onSuccess }: PreRegistroFormModalProps) {
  const { addToast } = useToast();
  const { isAdmin } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Salas disponibles
  const { data: salasRaw } = useCollection<{ id: string; nombre: string }>(
    'salas',
    [where('activa', '==', true)],
  );
  const salas = salasRaw || [];

  const updateField = <K extends keyof typeof EMPTY_FORM>(field: K, value: (typeof EMPTY_FORM)[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleSala = (salaId: string) =>
    setForm((f) => ({
      ...f,
      salasAsignadas: f.salasAsignadas.includes(salaId)
        ? f.salasAsignadas.filter((s) => s !== salaId)
        : [...f.salasAsignadas, salaId],
    }));

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    onClose();
  };

  const rolElegido = form.role !== '';
  // El Administrador y el Notificador no atienden pacientes: no requieren
  // registro de idoneidad, especialidad, centro médico ni salas.
  const esAdmin = form.role === 'admin' || form.role === 'notificador';
  const esMedicoOSupervisor = form.role === 'medico' || form.role === 'supervisor';
  // Salas: solo el médico queda acotado a salas específicas. El Supervisor es
  // un rol de supervisión de TODO el sistema — siempre acceso total ('*'),
  // igual que el Administrador — así que no se le exige (ni se le respeta)
  // una selección de salas puntual.
  const requiereSalas = form.role === 'medico';
  // Administración y supervisión nunca se eximen: son quienes otorgan las
  // exenciones y operan todo el sistema. El backend lo rechaza igual.
  const puedeEximirse = form.role !== 'admin' && form.role !== 'supervisor';

  const canSubmit =
    rolElegido &&
    form.email.trim() !== '' &&
    form.nombre1.trim() !== '' &&
    form.apellido1.trim() !== '' &&
    form.cedula.trim() !== '' &&
    (esAdmin ||
      (form.colegiadoId.trim() !== '' &&
        form.especialidad !== '' &&
        form.centroMedico.trim() !== '' &&
        (!requiereSalas || form.salasAsignadas.length > 0))) &&
    // Eximir sin explicar por qué no deja rastro útil en la auditoría.
    (!(puedeEximirse && form.exentoSegundoFactor)
      || form.motivoExencionSegundoFactor.trim().length >= 10) &&
    !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const telefono = form.telefono.trim() || '+50600000000';
      const nombre = [form.nombre1.trim(), form.nombre2.trim()].filter(Boolean).join(' ');
      const apellidos = [form.apellido1.trim(), form.apellido2.trim()].filter(Boolean).join(' ');
      await preRegistrarFn({
        email: form.email,
        telefono,
        nombre,
        apellidos,
        cedula: form.cedula.trim(),
        colegiadoId: form.colegiadoId,
        seisMedicoId: form.seisMedicoId,
        especialidad: form.especialidad,
        seisEspecialidadId: form.seisEspecialidadId,
        subespecialidad: form.subespecialidad,
        centroMedico: form.centroMedico,
        seisInstalacionId: form.seisInstalacionId,
        role: form.role,
        salasAsignadas: form.salasAsignadas,
        exentoSegundoFactor: puedeEximirse && form.exentoSegundoFactor,
        motivoExencionSegundoFactor: form.motivoExencionSegundoFactor.trim(),
      });
      addToast('success', `Usuario ${nombre} ${apellidos} pre-registrado exitosamente.`);
      setForm(EMPTY_FORM);
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al pre-registrar';
      addToast('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={resetAndClose} title="Pre-registrar usuario" size="lg">
      <form className={styles.form} onSubmit={handleSubmit}>
        <p className={styles.intro}>
          <UserPlus size={14} aria-hidden="true" />
          Registre los datos del usuario autorizado. Recibirá una invitación para crear su cuenta.
        </p>

        {/* 1. ROL — primer campo; habilita el resto del formulario */}
        <div className={styles.field}>
          <label className={styles.label}>Rol *</label>
          <div className={styles.roleOptions}>
            <label className={styles.radio}>
              <input
                type="radio"
                name="role"
                value="medico"
                checked={form.role === 'medico'}
                onChange={() => updateField('role', 'medico')}
                disabled={submitting}
              />
              <span>Médico</span>
            </label>
            <label className={styles.radio}>
              <input
                type="radio"
                name="role"
                value="supervisor"
                checked={form.role === 'supervisor'}
                onChange={() => updateField('role', 'supervisor')}
                disabled={submitting}
              />
              <span>Supervisor</span>
            </label>
            {isAdmin && (
              <label className={styles.radio}>
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={form.role === 'admin'}
                  onChange={() => updateField('role', 'admin')}
                  disabled={submitting}
                />
                <span>Administrador</span>
              </label>
            )}
            <label className={styles.radio}>
              <input
                type="radio"
                name="role"
                value="notificador"
                checked={form.role === 'notificador'}
                onChange={() => updateField('role', 'notificador')}
                disabled={submitting}
              />
              <span>Gestión de notificaciones</span>
            </label>
          </div>
        </div>

        {rolElegido && (
          <>
            <div className={styles.grid}>
              {/* 2. Email institucional */}
              <Input
                label="Email institucional *"
                type="email"
                placeholder="correo@ejemplo.com"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                disabled={submitting}
                required
              />
              {/* 3. Teléfono/celular */}
              <Input
                label="Teléfono/celular"
                type="tel"
                placeholder="+507 6000-0000"
                value={form.telefono}
                onChange={(e) => updateField('telefono', e.target.value)}
                disabled={submitting}
              />
              {/* 4. Nombres (1° y 2°) */}
              <Input
                label="Primer nombre *"
                placeholder="Ana"
                maxLength={MAX_30}
                value={form.nombre1}
                onChange={(e) => updateField('nombre1', e.target.value.slice(0, MAX_30))}
                disabled={submitting}
                required
              />
              <Input
                label="Segundo nombre"
                placeholder="Opcional"
                maxLength={MAX_30}
                value={form.nombre2}
                onChange={(e) => updateField('nombre2', e.target.value.slice(0, MAX_30))}
                disabled={submitting}
              />
              {/* 5. Apellidos (1° y 2°) */}
              <Input
                label="Primer apellido *"
                placeholder="López"
                maxLength={MAX_30}
                value={form.apellido1}
                onChange={(e) => updateField('apellido1', e.target.value.slice(0, MAX_30))}
                disabled={submitting}
                required
              />
              <Input
                label="Segundo apellido"
                placeholder="Opcional"
                maxLength={MAX_30}
                value={form.apellido2}
                onChange={(e) => updateField('apellido2', e.target.value.slice(0, MAX_30))}
                disabled={submitting}
              />
              {/* 6. Cédula */}
              <Input
                label="Cédula *"
                placeholder="8-888-8888"
                maxLength={MAX_30}
                value={form.cedula}
                onChange={(e) => updateField('cedula', soloAlfanumericoGuiones(e.target.value))}
                disabled={submitting}
                required
              />

              {esMedicoOSupervisor && (
                <>
                  {/* 7. No. de Registro de Idoneidad */}
                  <Input
                    label="No. de Registro de Idoneidad *"
                    placeholder="MED-12345"
                    maxLength={MAX_30}
                    value={form.colegiadoId}
                    onChange={(e) => updateField('colegiadoId', soloAlfanumericoGuiones(e.target.value))}
                    disabled={submitting}
                    required
                  />
                  {/* 8. Código de médico en HCIS */}
                  <Input
                    label="Código de médico en HCIS"
                    placeholder="Ej. 407718"
                    inputMode="numeric"
                    maxLength={MAX_30}
                    value={form.seisMedicoId}
                    onChange={(e) => updateField('seisMedicoId', soloDigitos(e.target.value))}
                    disabled={submitting}
                  />
                  {/* 9. Especialidad */}
                  <div className={styles.field}>
                    <label className={styles.label}>Especialidad *</label>
                    <select
                      className={styles.select}
                      value={form.especialidad}
                      onChange={(e) => updateField('especialidad', e.target.value)}
                      disabled={submitting}
                      required
                    >
                      <option value="">Seleccione…</option>
                      {ESPECIALIDADES.map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* 10. Código de especialidad en SEIS */}
                  <Input
                    label="Código de especialidad en SEIS"
                    placeholder="Ej. 4573"
                    inputMode="numeric"
                    maxLength={MAX_30}
                    value={form.seisEspecialidadId}
                    onChange={(e) => updateField('seisEspecialidadId', soloDigitos(e.target.value))}
                    disabled={submitting}
                  />
                  {/* 11. Subespecialidad */}
                  <Input
                    label="Subespecialidad"
                    placeholder="Opcional"
                    maxLength={MAX_30}
                    value={form.subespecialidad}
                    onChange={(e) => updateField('subespecialidad', e.target.value.slice(0, MAX_30))}
                    disabled={submitting}
                  />
                  {/* 12. Centro Médico */}
                  <Input
                    label="Centro Médico *"
                    placeholder="Hospital Nacional"
                    maxLength={MAX_30}
                    value={form.centroMedico}
                    onChange={(e) => updateField('centroMedico', e.target.value.slice(0, MAX_30))}
                    disabled={submitting}
                    required
                  />
                  {/* 13. Código de instalación de salud en SEIS */}
                  <Input
                    label="Código de instalación de salud en SEIS"
                    placeholder="Ej. 136"
                    inputMode="numeric"
                    maxLength={MAX_30}
                    value={form.seisInstalacionId}
                    onChange={(e) => updateField('seisInstalacionId', soloDigitos(e.target.value))}
                    disabled={submitting}
                  />
                </>
              )}
            </div>

            {/* 14. Salas asignadas — solo médico queda acotado a salas puntuales */}
            {!requiereSalas ? (
              <p className={styles.noSalas}>
                {form.role === 'notificador'
                  ? 'El gestor de notificaciones solo accede al módulo de campañas push: no atiende pacientes y no requiere registro de idoneidad, especialidad ni salas.'
                  : form.role === 'supervisor'
                    ? 'El Supervisor es un rol de supervisión de todo el sistema: tendrá acceso total (todas las salas), sin importar qué marque aquí.'
                    : 'El Administrador no atiende pacientes: tendrá acceso total (todas las salas) y no requiere número de registro de idoneidad, especialidad ni asignación de salas.'}
              </p>
            ) : (
              <div className={styles.field}>
                <label className={styles.label}>
                  Salas asignadas * ({form.salasAsignadas.length} seleccionadas)
                </label>
                <div className={styles.salasGrid}>
                  {salas.map((sala) => (
                    <label key={sala.id} className={styles.salaCheck}>
                      <input
                        type="checkbox"
                        checked={form.salasAsignadas.includes(sala.id)}
                        onChange={() => toggleSala(sala.id)}
                        disabled={submitting}
                      />
                      <span>{sala.nombre}</span>
                    </label>
                  ))}
                  {salas.length === 0 && (
                    <p className={styles.noSalas}>
                      No hay salas activas. Cree salas en /admin/salas primero.
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.field}>
          <label className={styles.label}>Segundo paso de autenticación</label>
          {puedeEximirse ? (
            <>
              <label className={styles.salaCheck}>
                <input
                  type="checkbox"
                  checked={form.exentoSegundoFactor}
                  onChange={(e) => updateField('exentoSegundoFactor', e.target.checked)}
                  disabled={submitting}
                />
                <span>No exigir segundo paso (2FA) a esta persona</span>
              </label>
              <p className={styles.noSalas}>
                Por omisión el portal pide un código de seis dígitos además de la contraseña.
                Marque esta casilla solo si esta persona no puede usar una aplicación de
                autenticación. La exención aplica desde su primer ingreso y se puede retirar
                después en Auditoría → Seguridad de acceso.
              </p>
              {form.exentoSegundoFactor && (
                <Input
                  label="Motivo de la exención *"
                  placeholder="Por ejemplo: no cuenta con equipo donde instalar la aplicación"
                  value={form.motivoExencionSegundoFactor}
                  onChange={(e) => updateField('motivoExencionSegundoFactor', e.target.value)}
                  disabled={submitting}
                  maxLength={200}
                />
              )}
            </>
          ) : (
            <p className={styles.noSalas}>
              El rol {form.role === 'admin' ? 'Administrador' : 'Supervisor'} siempre requiere
              segundo paso: administra el sistema y no admite exención.
            </p>
          )}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.btnSecondary} onClick={resetAndClose}>
            Cancelar
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={!canSubmit}>
            {submitting ? (
              'Registrando…'
            ) : (
              <>
                <CheckCircle2 size={16} aria-hidden="true" /> Pre-registrar usuario
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
